/**
 * Unlock state and the hidden activation gesture for the in-app dev tools overlay.
 *
 * The overlay leaves no visible trace until it is unlocked — no button, no badge — so a support user
 * sharing their screen never sees it. Two ways in:
 *
 * - **F12**, matching the browser reflex. Free to take: acceo_web.exe exposes no inspector of its own,
 *   and neither Frm_Main nor the ComptaAI host window binds the key, so nothing upstream competes for
 *   it.
 * - **Five taps in the bottom-left corner** of the viewport within two seconds. Kept as a fallback,
 *   because the key still has to cross an out-of-process browser shell to reach the page — and the
 *   bottom-left corner is the one region the app never puts controls in.
 * - `?devtools=1` on any URL, which is adopted into the persisted flag on the next boot. `?devtools=0`
 *   locks it again, for when the gesture cannot reach the page.
 *
 * Each trigger toggles: fire it again to put the overlay away.
 *
 * Both watchers take an `isEnabled` predicate and consult it before doing anything observable. This is
 * not defence in depth — the F12 handler cancels the event, and F12 is page-cancellable in Chromium and
 * Firefox, so a listener that ran outside the Acomba host would stop a developer's real DevTools from
 * opening on `local` and `dev`.
 *
 * The flag lives in localStorage so it survives the OIDC round-trip — acceo_web.exe may open the
 * callback in a different tab, which rules out sessionStorage.
 */

const UNLOCK_KEY = 'devtools-unlocked';
const UNLOCK_QUERY_PARAM = 'devtools';

const REQUIRED_TAPS = 5;
const TAP_WINDOW_MS = 2000;

/** Side length of the square hot zone in the bottom-left corner, in CSS pixels. */
const CORNER_SIZE_PX = 64;

/**
 * What the current URL asks for: `devtools=1` unlocks, `devtools=0` locks, anything else asks nothing.
 * The answer is adopted into the persisted flag at init.
 */
export function readUnlockRequestFromQuery(): boolean | null {
    const requested = new URLSearchParams(window.location.search).get(UNLOCK_QUERY_PARAM);
    if (requested === '1') return true;
    if (requested === '0') return false;
    return null;
}

/** Whether the overlay is currently unlocked for this profile. */
export function isDevToolsUnlocked(): boolean {
    try {
        return localStorage.getItem(UNLOCK_KEY) === '1';
    } catch {
        return false;
    }
}

/** Persists the unlock flag. Storage failures leave the overlay in its current state. */
export function setDevToolsUnlocked(unlocked: boolean): void {
    try {
        if (unlocked) {
            localStorage.setItem(UNLOCK_KEY, '1');
            return;
        }
        localStorage.removeItem(UNLOCK_KEY);
    } catch {
        // Private mode / quota — the overlay simply won't remember the toggle across reloads.
    }
}

function isInCornerHotZone(event: PointerEvent): boolean {
    return event.clientX <= CORNER_SIZE_PX
        && event.clientY >= window.innerHeight - CORNER_SIZE_PX;
}

/**
 * Listens for the corner-tap gesture and invokes `onGesture` each time it completes, as long as
 * `isEnabled` says the overlay is allowed here.
 *
 * Listens passively and never calls preventDefault, so taps still reach whatever is underneath.
 * Returns a cleanup function that detaches the listener.
 */
export function watchUnlockGesture(onGesture: () => void, isEnabled: () => boolean): () => void {
    let taps: number[] = [];

    const handlePointerDown = (event: PointerEvent): void => {
        if (!isEnabled() || !isInCornerHotZone(event)) return;

        const now = Date.now();
        taps = [...taps.filter((at) => now - at < TAP_WINDOW_MS), now];
        if (taps.length < REQUIRED_TAPS) return;

        taps = [];
        onGesture();
    };

    window.addEventListener('pointerdown', handlePointerDown, { passive: true });
    return () => window.removeEventListener('pointerdown', handlePointerDown);
}

/**
 * Listens for F12 and invokes `onShortcut` on each press, as long as `isEnabled` says the overlay is
 * allowed here.
 *
 * Bound in the capture phase so no app-level key handler can swallow it first, and cancelled so the host
 * shell sees nothing. Modified presses (Ctrl+F12 and friends) and auto-repeat from a held key are
 * ignored — only a deliberate tap toggles.
 *
 * Returns a cleanup function that detaches the listener.
 */
export function watchUnlockShortcut(onShortcut: () => void, isEnabled: () => boolean): () => void {
    const handleKeyDown = (event: KeyboardEvent): void => {
        if (event.key !== 'F12' || event.repeat) return;
        if (event.ctrlKey || event.altKey || event.shiftKey || event.metaKey) return;
        // Before preventDefault, never after: outside the Acomba host this key belongs to the browser.
        if (!isEnabled()) return;

        event.preventDefault();
        onShortcut();
    };

    window.addEventListener('keydown', handleKeyDown, { capture: true });
    return () => window.removeEventListener('keydown', handleKeyDown, { capture: true });
}
